<!--  This component handles the back and forth of address searching, selecting, and availability.
      It emits values when a serviceable address has been found and selected. -->
<template>
  <div>
    <b-overlay :show="searching_address || adding_to_contact_list" no-wrap fixed/>

    <!-- Main search header and bar -->
    <div id="fiber-search-header">
      <div class="container pb-5">
        <h1 class="pt-4">XMission Fiber for home</h1>
        <h5 class="not-bold">Check your address availability</h5>
        <address-auto-complete :init-display-value="initialAddressString" @address-selected="setAddressSelected"></address-auto-complete>
      </div>
    </div>

    <div class="container" v-if="error_message">
      <div class="alert alert-danger" role="alert">
        {{ error_message }}
      </div>
    </div>

    <!-- XWi Promo Banner to hide empty whitespace -->
    <div id="whitespace" v-if="!serviceable_address_selected" class="container">
      <p class="mt-4 pt-3 ml-4 pl-2"><span class="rotated-90" style="margin-left:-28px">&laquo;</span>To get started, enter the address where you would like to have XMission Fiber.<span class="d-block mt-2">For more information visit <a href="https://xmission.com/xmission-fiber" target="_blank" class="btn btn-outline-secondary btn-sm">XMission Fiber</a> or contact <a href="https://xmission.com/contact" target="_blank" class="btn btn-outline-secondary btn-sm">XMission sales</a></span></p>
      <div id="xwi-promo">
        <table>
          <tbody>
            <tr>
              <td>
                <img src="https://asset.xmission.com/xwi/xwi-router-app.png" class="d-none d-md-block"  alt="XWi router app"/>
              </td>
              <td>
                <small>When choosing your connectivity package</small>
                <h4 class="mb-0 pb-0">Insist on <img src="https://asset.xmission.com/xwi/xwi-logo-white.png" alt="XWi" style="height:20px; vertical-align:baseline"> Home</h4>
                <br />
                <div class="features-box">
                  <b>Features include:</b>
                  <ul class="mt-1">
                    <li>Complete home Internet solution with state-of-the-art router</li>
                    <li>Easy-to-use smartphone app to control your settings</li>
                    <li>Hassle-free remote support from XMission techs 24/7</li>
                    <li><b>Save on XMission Fiber</b> when you use XWi Home</li>
                  </ul>
                  <a href="https://xmission.com/xwi" target="_blank">
                    <button class="btn btn-outline-dark">Learn more</button>
                  </a>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Sometimes the address searched did not return a unique address, display the modal if it is not unique so the customer can select the exact address -->
    <b-modal v-model="show_multi_address_modal" @show="address_id_selected = ''">
      <template #modal-header>
        <div>
          <h4 class="modal-title">Suggested serviceable addresses</h4>
          <small>(if your address is not listed, you may be outside a serviceable area)</small>
        </div>
      </template>
      <template #default>
        <div v-for="address of addresses" :key="address.address_id">
          <div class="form-group">
            <label>
              <input type="radio" class="NSradioBtn" name="address-radio" @click="setAddressDetails(address)">
              <span>{{ address.address.formatted_address }}</span>
            </label>
          </div>
        </div>
      </template>
      <template #modal-footer>
        <button type="button" class="btn btn-secondary" @click="show_multi_address_modal = false;">Cancel</button>
        <button type="button" class="btn btn-danger" @click="show_multi_address_modal = false; setNoAddressFound()">My address is not listed</button>
        <button type="button" class="btn btn-success" @click="getServiceabilityDetails(); show_multi_address_modal = false;" :disabled="address_id_selected === ''">Select Address</button>
      </template>
    </b-modal>

    <!-- Display a modal asking for details if the address is not yet serviceable -->
    <xm-modal
      v-model="show_address_unavailable_modal"
      @show="address_id_selected = ''"
      title="Hmm, we couldn't find you."
      :save="addToContactList"
      save-text="Add me"
      :save-disabled="!serviceability_notice_form_valid"
      :cancel-method="cancelAddToContactList">
      <div>
        <b>{{ serviceability_notice_form.address }}</b> isn’t available... yet<br><br>
        Did you just receive a card from us in the mail?  Service may be coming very soon. Fill out the form below and we’ll get in touch quickly to complete the process. <br><br>
        Just interested?  Leave us your contact info below and we will notify you the moment your address becomes serviceable.
        <form-validator v-model="serviceability_notice_form_valid">
          <xm-input v-model="serviceability_notice_form.name" name="name" label="Full Name" required></xm-input>
          <xm-input v-model="serviceability_notice_form.phone" name="phone" label="Phone Number" :pattern="regex.phone" v-mask="'(###) ###-####'" required></xm-input>
          <xm-input v-model="serviceability_notice_form.email" name="email" label="Email Address" type="email" :pattern="regex.email" required></xm-input>
        </form-validator>
      </div>
    </xm-modal>
  </div>
</template>


<script>
  import AddressAutoComplete from './AddressAutoComplete.vue';
  import { BOverlay, BModal } from "bootstrap-vue";
  import FormValidator from '../FormValidator.vue';
  import XmInput from '../XmInput.vue';
  import XmModal from "../Modals/XmModal.vue";
  import * as types from "../../store/mutation-types";
  import req from "../../lib/authRequest";
  import reg from "../../lib/regex";
  import ConnectivityClotService from "../../lib/ssu/input_models/connectivity-clot-service";
  import logger from "../../lib/logger";

  export default {
    data () {
      return {
        regex: reg,
        searching_address: false,
        serviceable_address_selected: false,
        addresses: [],
        show_multi_address_modal: false,
        show_address_unavailable_modal: false,
        address_id_selected: '',
        address_prevalidated: true,
        serviceability_notice_form: {name: '', address: '', email: '', phone: ''},
        serviceability_notice_form_valid: false,
        adding_to_contact_list: false,
        recatpcha_site_key: '6Le3_GEmAAAAALmjy7zJ1OluUj5NeIBuX_uRNJsM',
        address_potentially_serviceable: false,
        error_message: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip_code: '',
        formatted_address: '',
        pre_validated: false,
        form_data: {
          service: null,
        }
      }
    },

    emits: ['form-state', 'form-invalid', 'form-valid', 'input'],
    props : ['initial_data'],

    components: {
      AddressAutoComplete,
      BOverlay,
      BModal,
      FormValidator,
      XmInput,
      XmModal,
    },

    computed : {
      addressPotentiallyServiceable () {
        return this.address_potentially_serviceable;
      },
      formData () {
        return this.form_data;
      },
      initialAddressString() {
        return this.formatted_address;
      },
      addressIdSelected() {
        return this.address_id_selected;
      }
    },

    mounted () {
      this.initFromAddress();
    },

    methods: {
      initFromAddress (){
        if (this.initial_data === undefined || this.initial_data === null){
          return;
        }
        this.setAddressDetails(this.initial_data);
        this.addresses = [this.initial_data.address];
        this.getServiceabilityDetails();
      },
      addToContactList() {
        this.adding_to_contact_list = true;
        let self = this;
        return new Promise(() => {
          grecaptcha.ready(() => {
            grecaptcha.execute(self.recatpcha_site_key, {action: 'submit'}).then((token) => {
              self.serviceability_notice_form['recaptcha_token'] = token;
              req.post({
                url:'/connectivity_clots/availability',
                data: self.serviceability_notice_form,
                success(){
                  self.resetForm();
                  self.adding_to_contact_list = false;
                },
                error(err) {
                  self.resetForm();
                  self.adding_to_contact_list = false;
                  logger.debug(err);
                },
              })
            })
          });
        });
      },
      cancelAddToContactList() {
        let self = this;
        return new Promise((resolve, reject) => { self.show_address_unavailable_modal = false; })
      },
      setNoAddressFound () {
        this.address_id_selected = '';
        this.show_address_unavailable_modal = true;
        this.serviceability_notice_form.address = this.formatted_address;
      },
      setAddressSelected(address_data_selected) {
        logger.debug('address selected', address_data_selected)
        this.setAddressDetails(address_data_selected);
        this.searchServiceableAddress();
      },
      getAddressDetailsForSearch(){
        return {
          line1: this.line1,
          line2: this.line2,
          city: this.city,
          state: this.state,
          zip_code: this.zip_code,
          pre_validated: this.pre_validated
        }
      },
      searchServiceableAddress(){
        this.address_prevalidated = false;
        this.searching_address = true;
        // we only want one notification at a time
        this.error_message = '';
        let self = this;
        req.get({
          url:'/connectivity_clots/addresses',
          params: this.getAddressDetailsForSearch(),
          success(result){
            self.addresses = result.addresses
            if (result.addresses.length === 0){
              self.setNoAddressFound();
              self.searching_address = false;
            }
            else if (result.addresses.length === 1 && result.addresses[0].matches_search_address) {
              logger.debug('single address found', result.addresses[0])
              self.setAddressDetails(self.addresses[0]);
              self.getServiceabilityDetails();
            }
            else {
              self.show_multi_address_modal = true;
              self.searching_address = false;
            }
          },
          error(err) {
            self.searching_address = false;
          },
        })
      },
      setAddressDetails(address_details) {
        logger.debug('setAddressDetails', address_details)
        this.address_id_selected = address_details.address.address_id.trim();
        this.line1 = address_details.address.line1.trim();
        this.line2 = address_details.address.line2.trim();
        this.city = address_details.address.city.trim();
        this.state = address_details.address.state.trim();
        this.zip_code = address_details.address.zip_code.trim();
        this.formatted_address = address_details.address.formatted_address.trim();
      },
      getAvailabilityMessage(address_details) {
        let sales_email = 'sales@xmission.com';
        let sales_phone = '877.964.7746';
        let formatted_address = address_details.formatted_address;
        let message_end = `please contact XMission sales Toll-Free at ${sales_phone}, through online chat via the blue icon in the lower right corner, or email ${sales_email}.`;
        if (address_details.disabled) {
          return `There was an error checking the availability of the address ${formatted_address}. To order service, ${message_end}`;
        }
        else if (address_details.is_business) {
          return `The address ${formatted_address} appears to be for a business. For assistance, ${message_end}`;
        }
        else if (address_details.in_service) {
          return `The address ${formatted_address} appears to already be in service but through a different provider. If you would like to switch to XMission, or have any other questions, ${message_end}`;
        }
        else if (!address_details.orderable && !address_details.early_signup) {
          return `The address ${formatted_address} does not appear to be currently available. For assistance, ${message_end}`;
        }
        return '';
      },
      getServiceabilityDetails() {
        logger.debug('searching serviceability details for addresses', this.addresses);
        let address_details = this.addresses.filter(x => x.address.address_id === this.address_id_selected)[0];
        this.searching_address = true;
        let self = this;
        req.get({
          url:'/connectivity_clots/details',
          params: {service_class_id: address_details.service_class_id, address_id: address_details.address.address_id},
          success(service_details){
            self.setAddressDetails(address_details);
            let connectivity_clot = new ConnectivityClotService(service_details);
            if (!connectivity_clot.isAvailable()) {
              self.resetForm();
              self.error_message = self.getAvailabilityMessage(service_details.address_details);
              return
            }
            self.form_data.service = service_details;
            self.serviceable_address_selected = true;
            self.searching_address = false;
            self.address_potentially_serviceable = true;
          },
          error(err) {
            self.searching_address = false;
          }
        })
      },
      resetForm() {
        this.address_id_selected = '';
        this.address_prevalidated = true;
        this.error_message = '';
        this.serviceability_notice_form_valid = false;
        this.serviceability_notice_form = {name: '', address: '', email: '', phone: ''};
        this.show_address_unavailable_modal = false;
        this.show_multi_address_modal = false;
        this.searching_address = false;
        this.address_potentially_serviceable = false;
        this.addresses = [];
        this.adding_to_contact_list = false;
        this.line1 = '';
        this.line2 = '';
        this.city = '';
        this.state = '';
        this.zip_code = '';
        this.form_data = {
          service: null,
        };
        this.$store.commit(types.SET_SERVICE_CLASS_ORDER, ['connectivity', 'xwi', 'voice', 'subnet', 'static_ip']);
      }
    },

    watch: {
      addressPotentiallyServiceable (new_val) {
        this.$emit('form-state', new_val);
        new_val ? this.$emit('form-valid') : this.$emit('form-invalid');
      },
      formData: {
        handler(new_val) {
          this.$emit('input', new_val);
        },
        deep: true
      },
      addressIdSelected () {
        this.$store.commit(types.SELF_SIGNUP_RESET_OUTPUT_MODELS);
      }
    }

  }
</script>


<style scoped>
  label {
    font-weight: normal !important;
  }

  /* utility classes */
  .not-bold {font-weight:200}

  /* SS form styles */
  #fiber-search-header {
    padding-top:72px;
    color:white;
    background-size:cover;
    margin-bottom:25px;
    background-color:#444;
    background:url('https://asset.xmission.com/xmweb3/xm-sso-bg.jpg');
    background-size:cover;
    background-repeat:no-repeat;
    background-position:center center;
  }

/** whitespace promo **/

#whitespace .rotated-90 {
  font-size:50px;
  transform:rotate(90deg);
  display:inline-block;
  float:left;
  margin:-28px 0 0 0;
  color:#9EB231;
}

#xwi-promo {
  padding:40px;
  margin-top:40px;
  border-top:1px solid #ddd;
  opacity:0.75;
  filter: grayscale(0.6);
  transition:all 0.5s;
}

#xwi-promo:hover {
  filter:grayscale(0);
  transition:all 0.5s;
  opacity:1;
}

#xwi-promo img {
  opacity:0.8;
  transition:all 0.5s;
}

#xwi-promo:hover img {
  opacity:1;
  transition:all 0.5s;
}

.features-box {padding-left:20px}

@media screen and (max-width: 768px) {
  #xwi-promo .features-box {padding-left:0;}
}
</style>



// WEBPACK FOOTER //
// ServiceableAddressSearch.vue?27e92c6a